import React from "react";
import { withRouter } from "react-router-dom";
import { useQueryString } from "common/effects";
import { compose, withAppContext, withState, withActions } from "containers/HOC";
import { getFarmGroup } from "containers/FarmGroups/_actions";
import { getFarmsPublishedToMyOrg, getMyFarmTags } from "containers/FarmTags/_actions";
import { getReportDataByFarmGroup, getReportDataByAccount } from "./_actions";
import { useParams, useLocation } from "react-router-dom";
import * as utils from "common/utils";

export const withFarmGroup = (WrappedComponent) => {
    const WithFarmGroup = ({ getFarmGroup, ...rest }) => {
        const location = useLocation();
        const { farmGroupId } = useParams();

        const [state, setState] = React.useState();
        const [reloadTrigger, setReloadTrigger] = React.useState(false);

        // Fetch farm group on load and refresh
        React.useEffect(() => {
            let isMounted = true;

            const fetchFarmGroup = async (farmGroupId, getFarmGroup) => {
                try {
                    const farmGroup = await getFarmGroup(farmGroupId);
                    const availableYears = (farmGroup.summary || [])
                        .map((summary) => summary.year)
                        .sort()
                        .reverse();
                    const farms = farmGroup.memberFarms.map((member) => ({ id: member.farmId, name: member.farmName, address: member.farmAddress }));
                    if (isMounted) setState({ farmGroup, availableYears, farms });
                } catch {
                    if (isMounted) setState({ farmGroup: {}, availableYears: [], farms: [] });
                }
            };

            if (farmGroupId) {
                fetchFarmGroup(farmGroupId, getFarmGroup);
            }

            return () => (isMounted = false);
        }, [farmGroupId, location, reloadTrigger, getFarmGroup]);

        const wrappedProps = {
            ...rest,
            ...state,
            reloadFarmGroup: () => setReloadTrigger(!reloadTrigger),
        };
        return <WrappedComponent {...wrappedProps} />;
    };

    return compose(withActions({ getFarmGroup }))(WithFarmGroup);
};

export const withFarmsPublishedToMyOrg = (WrappedComponent) => {
    const WithFarmsPublishedToMyOrg = ({ getFarmsPublishedToMyOrg, ...rest }) => {
        const [farmsPublishedToMyOrg, setFarmsPublishedToMyOrg] = React.useState();

        React.useEffect(() => {
            let isMounted = true;

            const fetchData = async (getFarmsPublishedToMyOrg) => {
                try {
                    const farms = await getFarmsPublishedToMyOrg();
                    if (isMounted) setFarmsPublishedToMyOrg(farms);
                } catch {
                    if (isMounted) setFarmsPublishedToMyOrg([]);
                }
            };

            fetchData(getFarmsPublishedToMyOrg);

            return () => (isMounted = false);
        }, [getFarmsPublishedToMyOrg]);

        const wrappedProps = {
            ...rest,
            farms: farmsPublishedToMyOrg,
        };
        return <WrappedComponent {...wrappedProps} />;
    };

    return compose(
        withActions({ getFarmsPublishedToMyOrg }),
        withState((state) => {
            return {
                availableYears: (state.dashboard.myPublicationsSummary || [])
                    .map((publicationSummary) => publicationSummary.year)
                    .sort()
                    .reverse(),
            };
        })
    )(WithFarmsPublishedToMyOrg);
};

export const withAggregatedReporting = (WrappedComponent) => {
    const WithAggregatedReporting = ({ farmGroup, accountId, farmIds, year, includeLatestYearEnds, getReportDataByFarmGroup, getReportDataByAccount, refData, ...rest }) => {
        const location = useLocation();

        const [reportData, setReportData] = React.useState();
        const [reportDataError, setReportDataError] = React.useState();
        const { enterpriseTypes = [] } = refData;

        // Fetch aggregated report data
        React.useEffect(() => {
            let isMounted = true;

            const setEnterpriseName = (rd = {}) => {
                const { benchmarkedFarms = {} } = rd;
                const { farms = [] } = benchmarkedFarms;
                for (const farm of farms) {
                    const { enterprises = [] } = farm;
                    for (const enterprise of enterprises) {
                        enterprise.label = utils.valueToText(enterpriseTypes, enterprise.type);
                    }
                }
            };

            const fetchReportDataByFarmGroup = async (farmGroupId, farmIds, year, includeLatestYearEnds, getReportDataByFarmGroup) => {
                try {
                    const response = await getReportDataByFarmGroup(farmGroupId, farmIds, year, includeLatestYearEnds);
                    if (isMounted) {
                        response && setEnterpriseName(response);
                        setReportData(response || {});
                    }
                } catch {
                    if (isMounted) setReportDataError("An unexpected error has occured. Please try again or call support if the problem persists.");
                }
            };

            const fetchReportDataByAccount = async (accountId, farmIds, year, includeLatestYearEnds, getReportDataByAccount) => {
                try {
                    const response = await getReportDataByAccount(accountId, farmIds, year, includeLatestYearEnds);
                    if (isMounted) {
                        response && setEnterpriseName(response);
                        setReportData(response || {});
                    }
                } catch {
                    if (isMounted) setReportDataError("An unexpected error has occured. Please try again or call support if the problem persists.");
                }
            };

            if (year) {
                if (farmGroup) {
                    fetchReportDataByFarmGroup(farmGroup.id, farmIds, year, includeLatestYearEnds, getReportDataByFarmGroup);
                } else {
                    fetchReportDataByAccount(accountId, farmIds, year, includeLatestYearEnds, getReportDataByAccount);
                }
            }

            return () => (isMounted = false);
        }, [farmGroup, accountId, farmIds, year, includeLatestYearEnds, location, getReportDataByFarmGroup, getReportDataByAccount, enterpriseTypes]);

        const wrappedProps = {
            ...rest,
            farmIds,
            year,
            includeLatestYearEnds,
            farmGroup,
            reportData,
            reportDataError,
        };
        return <WrappedComponent {...wrappedProps} />;
    };

    return compose(
        withRouter,
        withAppContext,
        withActions({ getReportDataByFarmGroup, getReportDataByAccount }),
        withState((state, ownProps) => ({ accountId: ownProps.authContext.accountId })),
        withFilterByFarmTag,
        withFilterByYear
    )(WithAggregatedReporting);
};

const withFilterByFarmTag = (WrappedComponent) => {
    const WithFilterByFarmTag = ({ farms, getMyFarmTags, ...rest }) => {
        const [availableFarmTags, setAvailableFarmTags] = React.useState([]);
        const [farmsWithTags, setFarmsWithTags] = React.useState([]);
        const [farmTag, setFarmTag] = React.useState();
        const [farmIds, setFarmIds] = React.useState([]);
        const [reloadTrigger, setReloadTrigger] = React.useState(false);

        // Get the farm tags for the passed in farms
        React.useEffect(() => {
            let isMounted = true;

            const fetchData = async (farms, getMyFarmTags) => {
                try {
                    const myFarmTags = await getMyFarmTags();

                    const farmsWithTags = farms.map((farm) => {
                        const myFarmTag = myFarmTags.find((mft) => mft.farmId === farm.id) || {};
                        return {
                            ...farm,
                            tags: myFarmTag.tags || [],
                        };
                    });

                    const availableFarmTags = farmsWithTags
                        .reduce((tags, farmWithTags) => {
                            const newTags = farmWithTags.tags.filter((tag) => !tags.includes(tag));
                            return tags.concat(newTags);
                        }, [])
                        .sort((a, b) => (a > b ? 1 : -1));

                    if (isMounted) {
                        setFarmsWithTags(farmsWithTags);
                        setAvailableFarmTags(availableFarmTags);
                    }
                } catch {
                    if (isMounted) {
                        setFarmsWithTags([]);
                        setAvailableFarmTags([]);
                    }
                }
            };

            fetchData(farms, getMyFarmTags);

            return () => (isMounted = false);
        }, [farms, reloadTrigger, getMyFarmTags]);

        // farmIds is the list of farm id's that match the currently selected farm tag
        React.useEffect(() => {
            let isMounted = true;

            if (isMounted && farmsWithTags) {
                const selectedFarmIds = farmTag ? farmsWithTags.filter((farmWithTags) => farmWithTags.tags.includes(farmTag)).map((farmWithTags) => farmWithTags.id) : [];
                setFarmIds(selectedFarmIds);
            }

            return () => (isMounted = false);
        }, [farmsWithTags, farmTag]);

        const wrappedProps = {
            ...rest,
            farms,
            availableFarmTags,
            farmIds,
            onFarmTagChanged: (selectedFarmTag) => setFarmTag(selectedFarmTag),
            reloadFarmTags: () => setReloadTrigger(!reloadTrigger),
        };
        return <WrappedComponent {...wrappedProps} />;
    };
    return compose(withActions({ getMyFarmTags }))(WithFilterByFarmTag);
};

const withFilterByYear = (WrappedComponent) => {
    const WithFilterByYear = ({ availableYears, ...rest }) => {
        const queryString = useQueryString();
        const [year, setYear] = React.useState(queryString.get("year"));
        const [includeLatestYearEnds, setIncludeLatestYearEnds] = React.useState(false);

        // Set year when availableYears is loaded
        React.useEffect(() => {
            let isMounted = true;

            if (isMounted && availableYears) {
                const yearIsSetAndAvailable = year && availableYears.includes(Number(year));
                if (!yearIsSetAndAvailable) {
                    setYear(availableYears[0] || new Date().getFullYear());
                }
            }

            return () => (isMounted = false);
        }, [availableYears, year]);

        const onYearChanged = (selectedYear) => setYear(selectedYear);

        const onIncludeLatestYearEndChanged = (checked) => setIncludeLatestYearEnds(checked);

        const wrappedProps = {
            ...rest,
            availableYears,
            year,
            onYearChanged,
            includeLatestYearEnds,
            onIncludeLatestYearEndChanged,
        };
        return <WrappedComponent {...wrappedProps} />;
    };
    return WithFilterByYear;
};
